import React, { useState, useEffect, useRef } from 'react';
import mixpanel from 'mixpanel-browser';
import { ToastContainer } from 'react-toastify';

import { Link, TablePagination, Typography, FormControlLabel, Switch } from '@mui/material';

import { QuorumContact } from './QuorumContact';
import { LoanTermInfoSection } from './LoanTermInfoSection';
import { DisclaimersRow } from '../../Elements/DisclaimersRow';
import ToasterSurvey from '../../Elements/ToasterSurvey';
import { useToaster } from '../../Elements/useToaster';

import {
  FINANCE_AUTO_RATES,
  FINANCE_MORTGAGE_RATES,
  EXPLORE_RATES,
  SHOW_PAYMENT_OBJ,
} from '../../../constants';

import {
  AUTO,
  YR,
  MO,
  FINANCE_LENDERS_HEADER_TITLE,
  LOAN_TERMS,
  FEDERAL_CREDIT_UNION,
  NA,
  FCU,
  NO_RATES_AVAILABLE,
  LOGO_DISCLAIMER,
  RATES_DISCLAIMER,
  lenderObjMap,
} from '../../../constants/ComparisonSummary';

import { LENDERS_LOGO_OBJ } from '../Logos';

import './FinanceLenders.css';

export const FinanceLenders = ({
  isRatesTableFullScreen,
  lenders,
  selectedLoanTerms,
  financingType,
}) => {
  const containerRef = useRef(null);
  const [page, setPage] = useState(0);
  const [showPaymentType, setShowPaymentType] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [lenderByRow, setLendersByRow] = useState({ [page]: lenders });
  const len = Object.keys(selectedLoanTerms).length;
  const label = financingType === AUTO ? MO : YR;
  const objRatesKey = financingType === AUTO ? FINANCE_AUTO_RATES : FINANCE_MORTGAGE_RATES;
  const objLoanTermMonthKey = financingType === AUTO ? 'loanTermMonth' : 'mortgageLoanTermMonth';

  //call toast
  useToaster(containerRef, ToasterSurvey, 'survey-toast');

  useEffect(() => {
    const lenderNames = Object.keys(lenders);

    const currentLenderRow = {};
    let count = 0;

    while (lenderNames.length) {
      const rowOfLenders = lenderNames.splice(0, rowsPerPage);
      const lendersRow = rowOfLenders.reduce((acc, curr) => {
        acc[curr] = lenders[curr];
        return acc;
      }, {});
      currentLenderRow[count] = lendersRow;
      count++;
    }
    setLendersByRow(currentLenderRow);
  }, [rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    mixpanel.track(`Rates Info ${financingType}`, {
      path: window?.location?.pathname,
      oldPage: page,
      newPage,
      rowsPerPage,
    });
    setPage(newPage);
  };

  const utm_campaign = new URLSearchParams(window.location.search).get('utm_campaign');

  const utm_campaign_type_name = new URLSearchParams(window.location.search).get('utm_email_type');

  const utm_lender_name = new URLSearchParams(window.location.search).get('utm_lender_name');

  const utm_learn_more = new URLSearchParams(window.location.search).get('utm_learn_more');

  useEffect(() => {
    if (utm_campaign && utm_campaign_type_name && utm_lender_name) {
      if (utm_learn_more) {
        handleExploreMoreRates({
          url: lenderObjMap[utm_lender_name]?.url,
          lenderName: lenderObjMap[utm_lender_name]?.name,
        });

        setTimeout(() => {
          window.location.href = lenderObjMap[utm_lender_name]?.url;
        }, 1000);
      }
    }
  }, []);

  const handleChangeRowsPerPage = (event) => {
    mixpanel.track(`Rates Info ${financingType}`, {
      path: window?.location?.pathname,
      newRowsPerPage: parseInt(event.target.value, 10),
      oldRowsPerPage: rowsPerPage,
    });
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExploreMoreRates = ({ url, lenderName }) => {
    mixpanel.track(`Rates Info ${financingType}`, {
      lenderName,
      url,
      path: window?.location?.pathname,
    });
  };

  if (!Object.keys(lenders).length) {
    return <div className="summary-no-rates-available">{NO_RATES_AVAILABLE}</div>;
  }

  return (
    <>
      <div
        className="finance-lenders-table-wrapper"
        id={isRatesTableFullScreen ? '' : 'finance-lenders-table-id-wrapper'}
      >
        <div className="finance-lenders-table-header-row-wrapper">
          <div className="finance-lender-header-column">{FINANCE_LENDERS_HEADER_TITLE}</div>

          <div className="finance-lender-loan-terms-wrapper">
            <div> {LOAN_TERMS} </div>

            <div className="loan-terms-header-row-wrapper">
              {Object.values(selectedLoanTerms).map((val) => (
                <div
                  className="loan-terms-header-row-section-wrapper"
                  style={{ width: `${100 / len}%` }}
                  key={val}
                >
                  {' '}
                  {val} {label}{' '}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div>
          {Object.keys(lenderByRow[page]).map((lenderName) => {
            return (
              <div key={lenderName} className="finance-lender-apr-row">
                <div className="finance-lender-name">
                  {' '}
                  <a
                    className="lender-logo-wrapper"
                    target="_blank"
                    href={lenders[lenderName]?.[0]?.[objRatesKey]?.url}
                  >
                    <img
                      src={LENDERS_LOGO_OBJ[lenderName.trim()]}
                      className="lender-logo"
                      alt={lenderName.replace(FEDERAL_CREDIT_UNION, FCU)}
                      onClick={() => {
                        mixpanel.track(`Rates Info ${financingType}`, {
                          lenderName,
                          url: lenders[lenderName]?.[0]?.[objRatesKey]?.url,
                          path: window?.location?.pathname,
                        });
                      }}
                    />
                  </a>
                </div>
                <div className="finance-lender-loan-term-apr-rates-row">
                  <div className="finance-lender-loan-term-apr-rates">
                    {Object.keys(selectedLoanTerms).map((k) => {
                      // Future TO DO - we should add the checks for the vehicle, military specials etc
                      const foundLoanTerm = (lenders[lenderName] || []).find((obj) => {
                        return Number(obj[objRatesKey]?.[objLoanTermMonthKey]) === Number(k);
                      });
                      let apr_rate = Number(foundLoanTerm?.[objRatesKey]?.updatedApr)
                        ? `${foundLoanTerm?.[objRatesKey]?.updatedApr}%`
                        : NA;

                      apr_rate =
                        isNaN(foundLoanTerm?.[objRatesKey]?.updatedApr) &&
                        foundLoanTerm?.[objRatesKey]?.updatedApr !== undefined ? (
                          <QuorumContact
                            url={lenders[lenderName]?.[0]?.[objRatesKey]?.url}
                            financingType={financingType}
                          />
                        ) : (
                          apr_rate
                        );

                      const monthly_payment = foundLoanTerm?.[objRatesKey]?.amortizationInfo
                        ?.monthlyPayment
                        ? `$${foundLoanTerm?.[objRatesKey]?.amortizationInfo?.monthlyPayment} / MO*`
                        : null;
                      const total_amount_paid = foundLoanTerm?.[objRatesKey]?.amortizationInfo
                        ?.totalAmountPaid
                        ? `$${foundLoanTerm?.[objRatesKey]?.amortizationInfo?.totalAmountPaid} TPA*`
                        : null;
                      return (
                        <LoanTermInfoSection
                          aprRate={apr_rate}
                          len={len}
                          monthlyPayment={monthly_payment}
                          totalAmountPaid={total_amount_paid}
                          financingType={financingType}
                          key={k}
                          showPaymentType={showPaymentType}
                        />
                      );
                    })}
                  </div>
                  <div className="explore-rates-link-wrapper">
                    <FormControlLabel
                      sx={{ p: 0, pt: '3px', ml: 0 }}
                      control={
                        <Switch
                          sx={{
                            '& .MuiSwitch-switchBase': {
                              p: 0,
                            },
                            '& .MuiSwitch-track': {
                              height: '21px',
                              width: '41px',
                            },
                            p: 0,
                            height: '21px',
                            width: '41px',
                          }}
                          checked={showPaymentType}
                          onChange={() =>
                            setShowPaymentType((prevState) => {
                              return Number(!prevState);
                            })
                          }
                        />
                      }
                      label={
                        <Typography sx={{ fontFamily: 'sans-serif', fontSize: '14px', pl: 0.5 }}>
                          {' '}
                          {SHOW_PAYMENT_OBJ[showPaymentType]}{' '}
                        </Typography>
                      }
                    />
                    <Link
                      href={lenders[lenderName]?.[0]?.[objRatesKey]?.url}
                      target="_blank"
                      underline="none"
                      sx={{
                        '&:hover': { boxShadow: 'none', textDecoration: 'none' },
                        '&:active': { boxShadow: 'none' },
                        p: 0.65,
                        mr: 0,
                        mb: 0,
                        borderRadius: '3.5px',
                        color: 'white',
                        backgroundColor: 'black',
                        textTransform: 'initial',
                        fontSize: '14px',
                        fontWeight: 600,
                      }}
                      onClick={() =>
                        handleExploreMoreRates({
                          url: lenders[lenderName]?.[0]?.[objRatesKey]?.url,
                          lenderName,
                        })
                      }
                    >
                      {EXPLORE_RATES}
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div ref={containerRef}> </div>
        {/* <ToastContainer /> */}
        <TablePagination
          component="div"
          count={Object.keys(lenders).length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </div>
      <DisclaimersRow LOGO_DISCLAIMER={LOGO_DISCLAIMER} RATES_DISCLAIMER={RATES_DISCLAIMER} />
    </>
  );
};
